import { Stack, useMediaQuery } from '@mui/material'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import ChevronRight from 'assets/icons/chevron-right.svg'
import {
  DesktopDisciplineContentContainer,
  Level1CategoriesButton,
  Level1CategoriesContainer,
  Level2CategoriesContainer,
  Level2CategoriesTitleContainer,
  SecondColumnContainer
} from 'components/Layout/Header/DisciplineContent/DesktopDisciplineContent/DesktopDisciplineContent.style'
import {
  DimmedBackground,
  Level2CategoryLink
} from 'components/Layout/Header/DisciplineContent/DisciplineContent.style'
import {
  BlackText,
  FlexSpaceBetweenContainer,
  VerticallySelfCenteredDiv
} from 'components/common/common.style'
import useComponentVisible from 'components/common/useComponentVisible'
import { raceCategoriesLevel1, urlParser } from 'helpers/raceCategoryLevels'
import { recordKeys } from 'helpers/records'
import { themeUtils } from 'styles/theme'

interface DisciplineContentProps {
  isOpen: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  maxWidth?: number
}

export default function DesktopDisciplineContent({
  isOpen,
  setOpen,
  maxWidth
}: DisciplineContentProps) {
  const [activeLevel1Category, setActiveLevel1Category] =
    useState<keyof typeof raceCategoriesLevel1>('RUNNING')

  const { t } = useTranslation('common')
  const { t: frenchT } = useTranslation('common', { lng: 'fr' })
  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(isOpen)
  const router = useRouter()

  const isMobile = useMediaQuery(themeUtils.maxWidth.md)
  // if a click is detected outside of container, set isOpen to false
  useEffect(() => {
    if (!isComponentVisible && !isMobile) setOpen(false)
  }, [isComponentVisible])

  // if any outside action sets isOpen to true, set component visibility to true
  useEffect(() => {
    if (isOpen && !isMobile) setIsComponentVisible(true)
  }, [isOpen])

  useEffect(() => {
    setIsComponentVisible(false)
  }, [router.asPath])

  return (
    <>
      <DimmedBackground $isVisible={isComponentVisible} />
      <div>
        <DesktopDisciplineContentContainer
          $isVisible={isComponentVisible}
          $maxWidth={maxWidth}
          ref={ref}
        >
          <Level1CategoriesContainer>
            {recordKeys(raceCategoriesLevel1).map((type) => {
              return (
                <Level1CategoriesButton
                  key={type}
                  $isSelected={type === activeLevel1Category}
                  onClick={() => setActiveLevel1Category(type)}
                >
                  <FlexSpaceBetweenContainer>
                    <VerticallySelfCenteredDiv>
                      <BlackText semiBold noMargin>
                        {t(type)}
                      </BlackText>
                    </VerticallySelfCenteredDiv>
                    <VerticallySelfCenteredDiv>
                      <ChevronRight />
                    </VerticallySelfCenteredDiv>
                  </FlexSpaceBetweenContainer>
                </Level1CategoriesButton>
              )
            })}
          </Level1CategoriesContainer>
          {recordKeys(raceCategoriesLevel1).map((categoryLevel1) => (
            <SecondColumnContainer
              key={categoryLevel1}
              style={{
                display:
                  activeLevel1Category === categoryLevel1 ? 'inline' : 'none'
              }}
            >
              <Stack p={2.25}>
                <Level2CategoriesTitleContainer>
                  <BlackText semiBold noMargin>
                    {t(categoryLevel1)}
                  </BlackText>
                </Level2CategoriesTitleContainer>
                <Level2CategoriesContainer>
                  <Level2CategoryLink
                    $isSelected={
                      router.query.typeOrDistance ===
                      raceCategoriesLevel1[categoryLevel1].url
                    }
                    href={`/${urlParser(
                      raceCategoriesLevel1[categoryLevel1].url
                    )}`}
                  >
                    <span>
                      <BlackText semiBold noMargin>
                        {t(`ALL_${categoryLevel1}`)}
                      </BlackText>
                    </span>
                  </Level2CategoryLink>

                  {raceCategoriesLevel1[categoryLevel1].level2Categories.map(
                    (elem) => (
                      <Level2CategoryLink
                        key={elem}
                        $isSelected={
                          router.query.typeOrDistance ===
                          urlParser(frenchT(elem))
                        }
                        href={`/${urlParser(frenchT(elem))}`}
                      >
                        <BlackText semiBold noMargin>
                          <span>{t(elem)}</span>
                        </BlackText>
                      </Level2CategoryLink>
                    )
                  )}
                </Level2CategoriesContainer>
              </Stack>
            </SecondColumnContainer>
          ))}
        </DesktopDisciplineContentContainer>
      </div>
    </>
  )
}
